import { datadogLogs } from '@datadog/browser-logs'

const REQUEST_TIMEOUT_MS = 10000
const IFRAME_LOAD_TIMEOUT_MS = 5000
const PROXY_READY_TIMEOUT_MS = 5000
const PING_INTERVAL_MS = 50

// eslint-disable-next-line no-undef
const frameLogLevel = ['local', 'development'].includes(DEPLOYMENT_ENVIRONMENT) ? 'info' : 'warn'

const frameLogger = datadogLogs.createLogger('authFrameLogger', {
  level: frameLogLevel,
  // eslint-disable-next-line no-undef
  handler: DD_LOGS_HANDLER || 'console',
  context: {
    domain: 'auth',
    component: 'frame-proxy',
  },
})

/**
 * Singleton iframe-based auth proxy for cross-origin authentication requests.
 * Uses postMessage to communicate with an iframe hosted on the identity service
 * that can make requests with HttpOnly cookies.
 *
 * Available operations:
 * - 'auth:status' - GET /lender/auth/status
 * - 'auth:login' - POST /lender/auth/login (form-urlencoded)
 * - 'auth:login-code' - POST /lender/auth/login/code (JSON)
 * - 'auth:login-token' - POST /lender/auth/login/token (JSON)
 * - 'auth:token' - POST /lender/auth/token (JSON)
 * - 'auth:logout' - POST /lender/auth/logout
 * - 'auth:accounts' - POST /lender/accounts (JSON)
 */
export class IframeAuthProxy {
  constructor() {
    this.iframe = null
    this.pendingRequests = new Map()
    this.requestId = 0
    this.readyPromise = null
    this.availableOperations = []
  }

  /**
   * @param {MessageEvent} event
   */
  handleMessage = (event) => {
    if (event.origin !== window.location.origin &&
      event.origin !== IDENTITY_SERVICE_ORIGIN) {
      return
    }

    const eventData = event.data
    if (!eventData || typeof eventData !== 'object') {
      // frameLogger.debug('[Lender Proxy] Discard event with no data', { handleMessageData: eventData })
      return
    }

    if (!eventData?.type) {
      // frameLogger.debug('[Lender Proxy] Discard event with no type', { handleMessageData: eventData })
      return
    }

    if (eventData.type === 'proxy-ready') {
      // Ignoring proxy-ready since we have a more robust handshake process with ping/pong and operations list
      return
    }

    // Handle pong response
    if (eventData.type === 'proxy-pong') {
      frameLogger.info('[Lender Proxy] Received proxy-pong', { handleMessageData: eventData })
      if (this.onPong) {
        this.onPong()
        this.onPong = null
      }
      return
    }

    // Handle operations list response
    if (eventData.type === 'proxy-operations') {
      frameLogger.info('[Lender Proxy] Received available operations from proxy', { handleMessageData: eventData })
      this.availableOperations = eventData.operations || []
      if (this.onOperations) {
        this.onOperations()
        this.onOperations = null
      }
      return
    }

    if (eventData.type !== 'proxy-response') {
      frameLogger.debug('[Lender Proxy] Received unknown event', { handleMessageData: eventData })
      return
    }

    // Handle proxy response
    const pending = this.pendingRequests.get(eventData.id)
    if (!pending) {
      frameLogger.warn('[Lender Proxy] Received response with no request', { handleMessageData: eventData })
      return
    }

    pending.resolve({
      success: eventData.success,
      status: eventData.status,
      statusText: eventData.statusText,
      data: eventData.data,
      error: eventData.error,
    })
  }

  /**
   * Send a message to the iframe proxy, ensuring the proxy is initialized first.
   * @param {Object} message - The message payload
   */
  async postMessage(message) {
    await this.init()
    frameLogger.debug('[Lender Proxy] Sending message:', { type: message?.type })
    this.iframe.contentWindow.postMessage(message, IDENTITY_SERVICE_ORIGIN)
  }

  /**
   * Initialize the iframe proxy. Idempotent — returns the same promise on subsequent calls.
   *
   * Two phases:
   * 1. Iframe load — create iframe, append to DOM, wait for onload
   * 2. Proxy handshake — poll with proxy-ping until proxy-pong,
   *    then request proxy-list-operations to confirm readiness
   *
   * @returns {Promise<void>}
   */
  init() {
    if (this.readyPromise) {
      return this.readyPromise
    }

    const initStartTime = performance.now()

    // Phase 1: iframe load
    const iframeLoadPromise = new Promise((resolve, reject) => {
      const loadTimeoutId = setTimeout(() => {
        frameLogger.error('[Lender Proxy] iframe timeout', { timeoutMs: IFRAME_LOAD_TIMEOUT_MS, elapsedMs: Math.round(performance.now() - initStartTime) })
        reject(new Error('Auth proxy iframe load timeout'))
      }, IFRAME_LOAD_TIMEOUT_MS)

      this.iframe = document.createElement('iframe')
      this.iframe.src = `${IDENTITY_SERVICE_URL}/lender/auth/frame`
      this.iframe.style.display = 'none'

      window.addEventListener('message', this.handleMessage)

      this.iframe.onerror = () => {
        clearTimeout(loadTimeoutId)
        frameLogger.error('[Lender Proxy] iframe onerror', { elapsedMs: Math.round(performance.now() - initStartTime) })
        reject(new Error('Auth proxy iframe failed to load'))
      }

      this.iframe.onload = () => {
        clearTimeout(loadTimeoutId)
        frameLogger.info('[Lender Proxy] iframe onload', { iframeLoadMs: Math.round(performance.now() - initStartTime) })
        resolve()
      }

      document.body.appendChild(this.iframe)
    })

    // Phase 2: after iframe loads, ping until pong, then get operations
    this.readyPromise = iframeLoadPromise.then(() => {
      let pingInterval
      const handshakePromise = new Promise((resolve) => {
        pingInterval = setInterval(() => {
          frameLogger.debug('[Lender Proxy] Sending ping', { elapsedMs: Math.round(performance.now() - initStartTime) })
          this.iframe.contentWindow.postMessage({ type: 'proxy-ping' }, IDENTITY_SERVICE_ORIGIN)
        }, PING_INTERVAL_MS)

        this.onPong = () => {
          clearInterval(pingInterval)
          frameLogger.info('[Lender Proxy] Received pong, requesting operations', { elapsedMs: Math.round(performance.now() - initStartTime) })
          this.iframe.contentWindow.postMessage({ type: 'proxy-list-operations' }, IDENTITY_SERVICE_ORIGIN)

          this.onOperations = () => {
            frameLogger.info('[Lender Proxy] Auth proxy ready', { initDurationMs: Math.round(performance.now() - initStartTime) })
            resolve()
          }
        }
      })

      let readyTimeoutId
      const timeoutPromise = new Promise((resolve, reject) => {
        readyTimeoutId = setTimeout(() => {
          frameLogger.error('[Lender Proxy] Proxy ready timed out', { timeoutMs: PROXY_READY_TIMEOUT_MS, elapsedMs: Math.round(performance.now() - initStartTime) })
          reject(new Error('Auth proxy ready timeout'))
        }, PROXY_READY_TIMEOUT_MS)
      })

      return Promise.race([
        handshakePromise.then((value) => {
          clearTimeout(readyTimeoutId)
          return value
        }),
        timeoutPromise,
      ]).finally(() => {
        clearInterval(pingInterval)
        this.onPong = null
        this.onOperations = null
      })
    })

    return this.readyPromise
  }

  /**
   * Execute an operation by name.
   * @param {string} operation - The operation name (e.g., 'auth:status')
   * @param {Object} [options] - Request options
   * @param {Object} [options.body] - Request body
   * @param {Object} [options.headers] - Request headers (only allowed headers will be forwarded)
   * @returns {Promise<{status: number, statusText: string, data: any}>}
   */
  async operation(operation, options = {}) {
    const id = this.requestId++

    const responsePromise = new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject })
    })

    await this.postMessage({
      type: 'proxy-request',
      id,
      operation,
      body: options.body,
      headers: options.headers,
    })

    // Start timeout after the message is sent, so it only covers the round-trip
    let requestTimeoutId
    const timeoutPromise = new Promise((resolve, reject) => {
      requestTimeoutId = setTimeout(() => {
        this.pendingRequests.delete(id)
        reject(new Error('Request timeout'))
      }, REQUEST_TIMEOUT_MS)
    })

    return Promise.race([
      responsePromise.then((value) => {
        clearTimeout(requestTimeoutId)
        this.pendingRequests.delete(id)
        return value
      }),
      timeoutPromise,
    ])
  }

  /**
   * Check if the proxy is healthy by sending a ping.
   * @returns {Promise<void>}
   */
  async ping() {
    await this.postMessage({ type: 'proxy-ping' })
  }

  /**
   * Request list of available operations from the proxy.
   */
  async listOperations() {
    await this.postMessage({ type: 'proxy-list-operations' })
  }
}

// Singleton instance
export const iframeAuthProxy = new IframeAuthProxy()

export default iframeAuthProxy
